const YOUTUBE_VIDEO_ID_PATTERN = /^[\w-]{11}$/;

const isYouTubeVideoId = (value: string | null | undefined): value is string =>
  typeof value === 'string' && YOUTUBE_VIDEO_ID_PATTERN.test(value);

const isYouTubeHost = (hostname: string): boolean => {
  const host = hostname.replace(/^www\./, '');
  return (
    host === 'youtu.be' ||
    host === 'youtube.com' ||
    host.endsWith('.youtube.com')
  );
};

const isWatchPath = (pathname: string): boolean =>
  pathname.replace(/\/$/, '') === '/watch';

/** Parse watch/embed start offset into seconds for the iframe `start` param. */
const parseYouTubeStartSeconds = (rawUrl: string): number | undefined => {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return undefined;
  }

  const startParam = parsed.searchParams.get('start');
  if (startParam && /^\d+$/.test(startParam)) {
    return Number(startParam);
  }

  const tParam =
    parsed.searchParams.get('t') ??
    /[#&]t=([^&]+)/.exec(parsed.hash)?.[1] ??
    null;
  if (!tParam) {
    return undefined;
  }

  if (/^\d+$/.test(tParam)) {
    return Number(tParam);
  }
  if (/^\d+s$/.test(tParam)) {
    return Number(tParam.slice(0, -1));
  }

  const durationMatch = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(tParam);
  if (durationMatch) {
    const hours = Number(durationMatch[1] ?? 0);
    const minutes = Number(durationMatch[2] ?? 0);
    const seconds = Number(durationMatch[3] ?? 0);
    const total = hours * 3600 + minutes * 60 + seconds;
    return total > 0 ? total : undefined;
  }

  return undefined;
};

export const parseYouTubeVideoId = (rawUrl: string): string | undefined => {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return undefined;
  }

  if (!isYouTubeHost(parsed.hostname)) {
    return undefined;
  }

  const host = parsed.hostname.replace(/^www\./, '');

  if (host === 'youtu.be') {
    const videoId = parsed.pathname.slice(1).split('/')[0];
    return isYouTubeVideoId(videoId) ? videoId : undefined;
  }

  if (isWatchPath(parsed.pathname)) {
    const videoId = parsed.searchParams.get('v');
    return isYouTubeVideoId(videoId) ? videoId : undefined;
  }

  const pathMatch = /^\/(embed|shorts|v)\/([^/?]+)/.exec(parsed.pathname);
  if (pathMatch?.[2]) {
    return isYouTubeVideoId(pathMatch[2]) ? pathMatch[2] : undefined;
  }

  return undefined;
};

export const toYouTubeEmbedUrl = (
  videoId: string,
  options?: { startSeconds?: number },
): string => {
  const params = new URLSearchParams({
    enablejsapi: '1',
    origin: window.location.origin,
  });
  if (options?.startSeconds !== undefined && options.startSeconds > 0) {
    params.set('start', String(options.startSeconds));
  }
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

export const YOUTUBE_EMBED_ORIGIN = 'https://www.youtube.com';

/** Tell the embed player to emit postMessage events (infoDelivery, onStateChange). */
export const subscribeYouTubeEmbedPostMessages = (
  iframe: HTMLIFrameElement,
): void => {
  iframe.contentWindow?.postMessage(
    JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }),
    YOUTUBE_EMBED_ORIGIN,
  );
};

export const getYouTubeEmbedUrl = (url: string): string | undefined => {
  const videoId = parseYouTubeVideoId(url);
  if (!videoId) {
    return undefined;
  }
  const startSeconds = parseYouTubeStartSeconds(url);
  return toYouTubeEmbedUrl(videoId, { startSeconds });
};
